import axios, { AxiosRequestConfig } from 'axios'
import { AgentPrecenseCodes } from '../Five9Context'
import { IS_PRODUCTION } from 'src/constants'
import { PhoneType } from 'src/Components/Worklist/types'

export interface Five9DataCenter {
  name: string
  uiUrls: Five9Url[]
  apiUrls: Five9Url[]
  loginUrls: Five9Url[]
  active: boolean
}

interface Five9Url {
  host: string
  port: string
  routeKey: string
  version: string
}

export interface Five9LoginResponse {
  tokenId: string
  sessionId: string
  orgId: string
  userId: string
  context: {
    cloudClientUrl: string
    cloudTokenUrl: string
    farmId: string
  }
  metadata: {
    freedomUrl: string
    dataCenters: Five9DataCenter[]
  }
}

export interface Five9ExceptionResponse {
  five9ExceptionDetail: {
    errorCode: number
    message: string
  }
}

export const formatFive9Error = (error: any): string => {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      if (isFive9ExceptionResponse(error.response.data)) {
        return `Five9 Error - Code: ${error.response.data.five9ExceptionDetail.errorCode}, Message: ${error.response.data.five9ExceptionDetail.message}`
      }
      return `${error.response.status}: ${
        error.response.statusText
      } - ${JSON.stringify(error.response.data)}`
    } else if (error.request) {
      return 'No response received from Five9 API.'
    } else {
      return `Request error: ${error.message}`
    }
  }
  return 'Unknown error occurred with Five9 API.'
}

export const isFive9ExceptionResponse = (
  data: unknown
): data is Five9ExceptionResponse => {
  return (
    typeof data === 'object' && data !== null && 'five9ExceptionDetail' in data
  )
}

enum Five9Urls {
  LOGIN = '/appsvcs/rs/svc/auth/login',
  LOGOUT = '/appsvcs/rs/svc/auth/logout',
  METADATA = '/appsvcs/rs/svc/auth/metadata?role=Agent',
  ROLES = '/appsvcs/rs/svc/auth/roles',
}

export const five9Login = async (
  email: string,
  password: string,
  axiosConfig: AxiosRequestConfig
): Promise<Five9LoginResponse | undefined> => {
  const url = Five9Urls.LOGIN
  const payload = {
    passwordCredentials: {
      username: email,
      password: password,
    },
    policy: 'AttachExisting',
    appKey: 'web-ui',
  }
  try {
    const response = await axios.post(url, payload, axiosConfig)
    const data = response.data as Five9LoginResponse
    return data
  } catch (error) {
    console.log('Five9 login error: ', error)
  }
}

export const five9LogoutAllDevices = async (
  axiosConfig: AxiosRequestConfig
) => {
  try {
    await axios.post(Five9Urls.LOGOUT, {}, axiosConfig)
  } catch (error) {
    console.log('Five9 logout error: ', error)
    alert(error)
  }
}

export const five9Metadata = async (
  axiosConfig: AxiosRequestConfig
): Promise<Five9LoginResponse> => {
  const url = Five9Urls.METADATA
  const response = await axios.get(url, axiosConfig)
  return response.data
}

export const five9GetRoles = async (axiosConfig: AxiosRequestConfig) => {
  const url = Five9Urls.ROLES
  const response = await axios.get(url, axiosConfig)
  return response.data
}

export enum LoginState {
  ACCEPT_NOTICE = 'ACCEPT_NOTICE',
  RELOGIN = 'RELOGIN',
  SELECT_SKILLS = 'SELECT_SKILLS',
  SELECT_STATION = 'SELECT_STATION',
  WORKING = 'WORKING',
  SPECIFY_CLIENT_TIMEZONE = 'SPECIFY_CLIENT_TIMEZONE',
}
export const five9GetLoginState = async (
  agentId: string,
  axiosConfig: AxiosRequestConfig
): Promise<LoginState> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/login_state`
  const response = await axios.get(url, axiosConfig)
  return response.data
}

export const five9SessionStart = async (
  agentId: string,
  stationId: string,
  axiosConfig: AxiosRequestConfig
) => {
  var timezoneOffset = new Date().getTimezoneOffset()
  const url = `/appsvcs/rs/svc/agents/${agentId}/session_start?timeZoneOffset=${timezoneOffset}`
  const payload = {
    stationId,
    stationType: 'SOFTPHONE',
  }
  const response = await axios.put(url, payload, axiosConfig)
  return response.data
}

export enum StationType {
  PSTN = 'PSTN',
  SOFTPHONE = 'SOFTPHONE',
  GATEWAY = 'GATEWAY',
  EMPTY = 'EMPTY',
}

export enum StationState {
  CONNECTED = 'CONNECTED',
  CONNECTING = 'CONNECTING',
  DISCONNECTED = 'DISCONNECTED',
}

export interface StationInfo {
  stationId: string
  stationType: StationType
  state: StationState
}
export const five9GetStation = async (
  agentId: string,
  axiosConfig: AxiosRequestConfig
): Promise<StationInfo | null> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/station`
  const response = await axios.get(url, axiosConfig)
  if (response.data.stationId) {
    return response.data as StationInfo
  }
  return null
}

export interface AgentPresenceInfo {
  currentState: {
    notReadyReasonCodeId: string
  }
}
export const five9GetPresence = async (
  agentId: string,
  axiosConfig: AxiosRequestConfig
): Promise<AgentPresenceInfo | null> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/presence`
  const response = await axios.get(url, axiosConfig)
  if (response.data.currentState.notReadyReasonCodeId) {
    return response.data as AgentPresenceInfo
  }
  return null
}

export const five9UpdatePresence = async (
  agentId: string,
  presence: AgentPresenceInfo['currentState'],
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/presence`
  const response = await axios.put(
    url,
    {
      ...presence,
      readyChannels:
        presence.notReadyReasonCodeId === AgentPrecenseCodes.READY
          ? ['CALL', 'VOICE_MAIL']
          : [],
    },
    axiosConfig
  )
  return response.data
}

export const five9GetSkills = async (
  agentId: string,
  axiosConfig: AxiosRequestConfig
): Promise<string[] | null> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/active_skills`
  const response = await axios.get(url, axiosConfig)
  if (response.data) {
    return response.data as string[]
  }
  return null
}

export const five9RestartStation = async (
  agentId: string,
  axiosConfig: AxiosRequestConfig
): Promise<LoginState> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/station/restart`
  const response = await axios.put(url, { value: null }, axiosConfig)
  return response.data
}

export interface ExternalCallDestinationInfo {
  number: string
  skipDNCCheck: boolean // will return error NUMBER_IN_DNC_LIST(10006)
  campaignId?: string //empty string will result in error
  contactId?: string // use if contact is saved in the contact list
  autoResolveDialingRules: boolean
  checkMultipleContacts: boolean
}

export const five9MakeExternalCall = async (
  agentId: string,
  callDestination: ExternalCallDestinationInfo,
  axiosConfig: AxiosRequestConfig
) => {
  // A call can be made only when no other active call exists.
  // The agent must have CAN_MAKE_CALLS_TO_EXTERNAL permission.
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/make_external_call`
  const response = await axios.post(url, callDestination, axiosConfig)
  // The response is supposed to be empty.
  // It will triggger a websocket event that contains the call id
  return response.data
}

// don't use five9DisconnectCall if you want to set disposition
// call five9DisconnectAndDisposeCall directly
export const five9DisconnectCall = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
) => {
  // This request applies to all participants, including conference participants.
  // If the call is not internal, the CAN_WRAPUP_CALL permission is required.
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/disconnect`
  const response = await axios.put(url, undefined, axiosConfig)
  return response.data
}

export const five9DisconnectAndDisposeCall = async (
  agentId: string,
  callId: string,
  dispositionId: string,
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo> => {
  // This request applies to all participants, including conference participants.
  // If the call is not internal, the CAN_WRAPUP_CALL permission is required.
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/dispose`
  const response = await axios.put(
    url,
    { dispositionId: dispositionId },
    axiosConfig
  )
  return response.data
}

export const five9AnswerCall = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/answer`
  const response = await axios.put(url, undefined, axiosConfig)
  return response.data
}

export const five9RejectCall = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/reject`
  const response = await axios.put(url, undefined, axiosConfig)
  return response.data
}

export const five9HoldCall = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/hold`
  const response = await axios.put(url, undefined, axiosConfig)
  return response.data
}

export const five9UnholdCall = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/unhold`
  const response = await axios.put(url, undefined, axiosConfig)
  return response.data
}

export const five9ParkCall = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/park`
  const response = await axios.put(url, undefined, axiosConfig)
  return response.data
}

export const five9RetrieveCall = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/retrieve`
  const response = await axios.put(url, undefined, axiosConfig)
  return response.data
}

export const five9EndCall = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
) => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/disconnect`
  const response = await axios.put(url, undefined, axiosConfig)
  return response.data
}

export interface Note {
  agentId: string
  author: string
  authorId: boolean
  creationTime: number
  id: string
  modificationTime: number
  name: string
  note: string
  socialItemId: string
  tenantId: number
}

export interface AuthorAccount {
  accountId: string
  authorId: string
  handleId: string
  handleName: string
  imageURL: string
  profileURL: string
  type: string
  updateTime: number
}

export interface ContactInfo {
  accounts: AuthorAccount[]
  fields: any
  id: string
  modificationTime: number
  notes: Note[]
  score: number
}

export interface ContactLookupResultInfo {
  contacts: ContactInfo[]
  cursor: string
  hasMore: boolean
}

export enum AddressType {
  AGENT = 'AGENT',
  CAMPAIGN = 'CAMPAIGN',
  EXTERNAL = 'EXTERNAL',
  SKILL = 'SKILL',
  VOICEMAIL = 'VOICEMAIL',
}

export enum Five9CallState {
  CONFERENCE_PARTICIPANT_CONSULTING = 'CONFERENCE_PARTICIPANT_CONSULTING',
  CONFERENCE_PARTICIPANT_RINGING = 'CONFERENCE_PARTICIPANT_RINGING',
  CONFERENCE_PARTICIPANT_TALKING = 'CONFERENCE_PARTICIPANT_TALKING',
  FINISHED = 'FINISHED',
  OFFERED = 'OFFERED',
  ON_HOLD = 'ON_HOLD',
  PARKED = 'PARKED',
  QUEUED = 'QUEUED',
  RINGING_ON_OTHER_SIDE = 'RINGING_ON_OTHER_SIDE',
  RINGING_ON_OUR_SIDE = 'RINGING_ON_OUR_SIDE',
  TALKING = 'TALKING',
  WRAP_UP = 'WRAP_UP', // wrap up means hung up and waiting to set disposition
}

export interface WorksheetInfo {
  id: string
  version: number
  answers: any
  duration: number
  finished: boolean
}

export interface CallInfo {
  id: string
  campaignId: string
  callType: string
  transfer: boolean
  userTransferringId: string
  internalReceiver: boolean
  contactLookupResult: ContactLookupResultInfo
  previewId: string
  customer: string
  priority: number
  createTime: number
  acceptTime: number
  averageHandleTime: number
  stateSince: number
  stateDuration: number
  billingTimestamp: number
  billingTime: number
  handleTimestamp: number
  handleTime: number
  talkTimestamp: number
  talkTime: number
  holdTimestamp: number
  holdTime: number
  parkTimestamp: number
  parkTime: number
  wrapupTimestamp: number
  wrapupTime: number
  startTimestamp: number
  duration: number
  afterCallWorkTimeout: number
  queueType: 'PERSONAL' | 'SKILL'
  state: Five9CallState
  addressType: AddressType
  conferenceDispositioning:
    | 'INITIATOR_DECIDES_WHO_DISPOSITION'
    | 'INITIATOR_DISPOSITION'
    | 'LAST_AGENT_DISPOSITION'
  maxParticipantsCountToLeaveConference: number
  activeContact: ContactInfo
  comment: string
  variables: any
  worksheet: WorksheetInfo
  recording: boolean
  recordingAllowed: boolean
  autoRecording: boolean
  ani: string
  dnis: string
  remoteNumber: string
  canRecycle: boolean
  autoAnswer: boolean
  hasIncompleteTransfer: boolean
  dispositionId: string
  ivrTransferModule: string
  callback: boolean
  callbackId: string
  transferPauseTimeout: number
}

export const five9GetCalls = async (
  agentId: string,
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo[]> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls`
  const response = await axios.get(url, axiosConfig)
  return response.data
}

export interface CampaignInfo {
  id: string
  name: string
  campaignType: 'INBOUND' | 'OUTBOUND' | 'AUTODIAL'
  customSource: string
  customIcon: string
  assistances: string[]
  attributes: string[]
  avatarURL: string
  skillIds: string[]
  state: 'NOT_RUNNING' | 'RESETTING' | 'RUNNING' | 'STARTING' | 'STOPPING'
  flags: string[]
}

export const five9GetOrgCampaigns = async (
  orgId: string,
  axiosConfig: AxiosRequestConfig
): Promise<CampaignInfo[]> => {
  const url = `/appsvcs/rs/svc/orgs/${orgId}/campaigns`
  const response = await axios.get(url, axiosConfig)
  return response.data
}

export const five9GetOrgReasonCodes = async (
  orgId: string,
  axiosConfig: AxiosRequestConfig
): Promise<any[]> => {
  const url = `/appsvcs/rs/svc/orgs/${orgId}/not_ready_reason_codes`
  const response = await axios.get(url, axiosConfig)
  return response.data
}

export interface CampaignTransferDestinationInfo {
  campaignId: string
  warm: boolean
}

// This is how ops warm-transfers to clinicians
export const five9TransferToCampaign = async (
  agentId: string,
  callId: string,
  destination: CampaignTransferDestinationInfo,
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/transfer_to_campaign`
  const response = await axios.put(url, destination, axiosConfig)
  return response.data
}

// completeTransfer will end the call on the transferer's side.
export const five9CompleteTransfer = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
): Promise<void> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/complete_transfer`
  await axios.put(url, undefined, axiosConfig)
}

export const five9AddExternalParticipant = async (
  agentId: string,
  callId: string,
  number: string,
  axiosConfig: AxiosRequestConfig,
  warm: boolean = false
) => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/add_external_to_conference`
  const response = await axios.post(url, { number, warm }, axiosConfig)
  return response.data
}

export const five9AddCampaignToConference = async (
  agentId: string,
  callId: string,
  campaignId: string,
  axiosConfig: AxiosRequestConfig,
  warm: boolean = false
) => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/add_campaign_to_conference`
  const response = await axios.post(url, { campaignId, warm }, axiosConfig)
  return response.data
}

export const five9CompleteWarmConference = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
) => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/complete_warm_conference`
  const response = await axios.post(url, undefined, axiosConfig)
  return response.data
}

export const five9CancelWarmTransfer = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
) => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/cancel_transfer`
  const response = await axios.put(url, undefined, axiosConfig)
  return response.data
}

export const five9ExternalTransfer = async (
  agentId: string,
  callId: string,
  number: string,
  warm: boolean,
  axiosConfig: AxiosRequestConfig
) => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/transfer_to_external_number`
  const response = await axios.put(
    url,
    { destination: { number }, warm },
    axiosConfig
  )
  return response.data
}

export const five9DisconnectConferenceParticipant = async (
  agentId: string,
  callId: string,
  axiosConfig: AxiosRequestConfig
) => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/disconnectConferenceParticipant`
  const response = await axios.put(url, undefined, axiosConfig)
  return response.data
}

export const five9LeaveConference = async (
  agentId: string,
  callId: string,
  dispositionId: string,
  axiosConfig: AxiosRequestConfig
) => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/leave_conference`
  const response = await axios.put(url, { dispositionId }, axiosConfig)
  return response.data
}

export interface CreateContactPhoneNumber {
  phoneNumber: string
  phoneType: PhoneType
}

export interface CreateContactPayload {
  firstName: string
  lastName: string
  phoneNumbers: CreateContactPhoneNumber[]
  state: string
  zipCode: string
  emrLink: string
  consultId: string
  programName: string
}

interface Five9CreateContactFields {
    fields: {
        [x: string]: string;
    };
}

const five9CreateContactHelper = async (
  orgId: string,
  createContactFields: Five9CreateContactFields,
  axiosConfig: AxiosRequestConfig
): Promise<ContactInfo> => {
  const url = `/appsvcs/rs/svc/orgs/${orgId}/contacts`
  const response = await axios.post(
    url,
    {
      ...createContactFields,
    },
    axiosConfig
  )
  return response.data
}

export const five9CreateContact = async (
  orgId: string,
  payload: CreateContactPayload,
  axiosConfig: AxiosRequestConfig
): Promise<ContactInfo> => {
  const fieldsObject = {
    fields: {
      [IS_PRODUCTION ? '1010' : '1000']: payload.firstName,
      [IS_PRODUCTION ? '1011' : '1001']: payload.lastName,
      [IS_PRODUCTION ? '1015' : '1005']: payload.state,
      [IS_PRODUCTION ? '1016' : '1006']: payload.zipCode,
      [IS_PRODUCTION ? '1060' : '300000000001001']: payload.emrLink,
      [IS_PRODUCTION ? '1080' : '300000000001022']: payload.consultId,
      [IS_PRODUCTION ? '1070' : '300000000001002']: payload.programName,
      ...(payload.phoneNumbers[0] && {
        [IS_PRODUCTION ? '1018' : '1008']: payload.phoneNumbers[0].phoneNumber,
      }),
      ...(payload.phoneNumbers[1] && {
        [IS_PRODUCTION ? '1019' : '1009']: payload.phoneNumbers[1].phoneNumber,
      }),
      ...(payload.phoneNumbers[2] && {
        [IS_PRODUCTION ? '1020' : '1010']: payload.phoneNumbers[2].phoneNumber,
      }),
    },
  }

  // Map phone numbers to their corresponding field IDs
  const phoneTypeToFieldId: Record<PhoneType, string | undefined> =
    IS_PRODUCTION
      ? {
          primary: '1100',
          sms: '1101',
          mobile: '1102',
          home: '1103',
          power_of_attorney: '1104',
          care_giver: '1105',
          family_member: '1106',
          other: '1107',
          authorized_representative: '1108',
        }
      : {
          primary: undefined,
          sms: undefined,
          mobile: undefined,
          home: undefined,
          power_of_attorney: undefined,
          care_giver: undefined,
          family_member: undefined,
          other: undefined,
          authorized_representative: '300000000001005',
        }

  const phoneFieldNames = IS_PRODUCTION ?
    ['1018', '1019', '1020', ...(Object.values(phoneTypeToFieldId).filter(x => x))] :
    ['1008', '1009', '1010', ...(Object.values(phoneTypeToFieldId).filter(x => x))]

  // Add phone numbers to fields object if they have a valid type
  payload.phoneNumbers.forEach((phone) => {
    const fieldId = phoneTypeToFieldId[phone.phoneType]
    if (fieldId) {
      fieldsObject.fields[fieldId] = phone.phoneNumber
    }
  })
  try {
    return await five9CreateContactHelper(
      orgId,
      fieldsObject,
      axiosConfig
    )
  } catch (error) {
    if (
      (error as any)?.response?.data?.five9ExceptionDetail?.errorCode ===
      12032 // invalid fields
    ) {
      const exceptionContext = (error as any)?.response?.data?.five9ExceptionDetail?.context
      const keys = Object.keys(exceptionContext)

      if (keys.some(key => !phoneFieldNames.includes(key))) {
        // if a non phone number field is causing the issue, throw
        throw error
      }

      // Create new fieldsObject without the invalid phone number fields
      const cleanedFields = Object.keys(fieldsObject.fields).reduce((acc, key) => {
        if (!keys.includes(key)) {
          acc[key] = fieldsObject.fields[key]
        }
        return acc
      }, {} as any)

      const cleanedFieldsObject = {
        ...fieldsObject,
        fields: cleanedFields
      }

      // Retry the contact creation with cleaned fields
      return await five9CreateContactHelper(
        orgId,
        cleanedFieldsObject,
        axiosConfig
      )
    }
    throw error
  }
}

interface Five9LookupContactData {
  fields: any[]
  id: string
}

interface Five9LookupContactResponse {
  contacts: Five9LookupContactData[]
}
export const five9GetContactByConsultId = async (
  orgId: string,
  consultId: string,
  axiosConfig: AxiosRequestConfig
): Promise<Five9LookupContactResponse> => {
  const url = `/appsvcs/rs/svc/orgs/${orgId}/contacts?_s=${consultId}`
  const response = await axios.get(url, axiosConfig)
  return response.data
}

export const five9DeleteContact = async (
  orgId: string,
  contactId: string,
  axiosConfig: AxiosRequestConfig
): Promise<ContactInfo> => {
  const url = `/appsvcs/rs/svc/orgs/${orgId}/contacts/${contactId}`
  const response = await axios.delete(url, axiosConfig)
  return response.data
}

export const five9SelectActiveContactById = async (
  agentId: string,
  callId: string,
  contactId: string,
  axiosConfig: AxiosRequestConfig
): Promise<CallInfo> => {
  const url = `/appsvcs/rs/svc/agents/${agentId}/interactions/calls/${callId}/active_contact_2`
  const response = await axios.put(url, { value: contactId }, axiosConfig)
  return response.data
}
